import { CheckCircle, Clock } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useReliableWebSocket from "../../../utils/useReliableWebSocket";

// Constants
import { api_url, ws_url } from "../../../constants";
import { tripsApi, getErrorMessage } from "../../../api";

// Assets
import RouteConnector from "../../../components/RouteConnector";
import notificationSound from "../../../static/audio/notification.mp3";
import chatIcon from "../../../static/images/chat-icon.png";
import defaultPfp from "../../../static/images/default-pfp.png";
import profileIcon from "../../../static/images/profile-icon.svg";

// Components
import ChatNotification from "../../../components/ChatNotification";
import FormCard from "../../../components/FormCard";
import LoadingBar from "../../../components/LoadingBar";
import PillButton from "../../../components/PillButton";
import RoundedEdgeButton from "../../../components/RoundedEdgeButton";

// Hooks
import { useToast } from "../../../hooks/useToast";

// Utils
import { appendChatToIndexedDB, clearAllChatsFromIndexedDB, updateChatHistoryInIndexedDB } from "../../../utils/chatHelper";
import { calculateTimeSinceUpdate, clearStoredETA, getStoredETA, storeETA } from "../../../utils/helpers";
import { subscribeToPush } from "../../../utils/pushHelper";

// Styles
import "../../../CSS/DriverFound.scss";
import classes from "./DriverFound.module.scss";

// Constants
const MAX_RESPONSE_TIME = 30; // 30 seconds
const DEFAULT_ESTIMATED_TIME = 7 * 60; // 7 mins
const MIN_NEGOTIATION_PRICE = 3;

const DriverFound = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { openToast } = useToast();

  // Refs to prevent stale closures
  const intervalRef = useRef(null);
  const responseTimerRef = useRef(null);

  // Core state
  const [trip, setTrip] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // UI state
  const price = trip?.final_price ?? trip?.nego_offer ?? trip?.price_offer ?? 0;
  const [remainingSeconds, setRemainingSeconds] = useState(MAX_RESPONSE_TIME);
  const [showNegotiationOptions, setShowNegotiationOptions] = useState(false);
  const [sharingMessageShown, setSharingMessageShown] = useState(() => {
    return localStorage.getItem(`sharingMessageShown_${tripId}`) === "true";
  });

  // ETA related state
  const [estimatedTime, setEstimatedTime] = useState(DEFAULT_ESTIMATED_TIME);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [etaProgress, setEtaProgress] = useState(0);
  const [isCalculatingEta, setIsCalculatingEta] = useState(true);

  // Info card state
  const [infoCard, setInfoCard] = useState({
    title: "Loading...",
    subtitle: "Please wait..."
  });

  // WebSocket setup
  const tripUrl = `${ws_url}/ws/trip/${tripId}/`;
  const imageUrl = `${api_url}/media/`;

  const { lastJsonMessage: tripMessage, sendJsonMessage: sendTripMessage } = useReliableWebSocket(tripUrl, {
    share: true,
    shouldReconnect: () => true,
    onError: (error) => console.error("Trip WebSocket error:", error),
    persistenceKey: `client_trip_queue_${tripId}`,
  });

  // Cleanup function
  const cleanup = useCallback((timerRef = null) => {
    const timers = timerRef ? [timerRef] : [intervalRef, responseTimerRef];
    timers.forEach(ref => {
      if (ref.current) {
        clearTimeout(ref.current);
        clearInterval(ref.current);
        ref.current = null;
      }
    });
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    const audio = new Audio(notificationSound);
    audio.play().catch(err => {
      console.warn("Sound play blocked by browser until user interacts:", err);
    });
  }, []);

  // Navigation handler
  const handleNavigation = useCallback((location, timer = 0) => {
    cleanup();
    setTimeout(() => navigate(location), timer * 1000);
  }, [navigate, cleanup]);

  const handleAccept = useCallback(() => {
    sendTripMessage({
      event: "accept_offer",
      trip_id: tripId,
    });
  }, [tripId, sendTripMessage]);

  const handleCancel = useCallback(() => {
    sendTripMessage({
      event: "cancel_trip",
      trip_id: tripId,
    });
  }, [tripId, sendTripMessage]);

  const handleNegotiate = useCallback((discount) => {
    const newPrice = price - discount;
    sendTripMessage({
      event: "make_negotiation_offer",
      data: JSON.stringify({
        trip_id: trip?.trip_id,
        nego_offer: newPrice
      }),
    });
  }, [price, trip?.trip_id, sendTripMessage]);

  // Fetch latest eta from session storage
  useEffect(() => {
    const saved = getStoredETA(tripId);
    if (saved) {
      setEstimatedTime(saved.eta);
      setIsCalculatingEta(false);
    }
  }, [tripId]);

  // Fetch initial trip details
  useEffect(() => {
    if (!tripId) return;

    const fetchTripDetails = async () => {
      try {
        setIsLoading(true);
        const data = await tripsApi.getTrip(tripId);

        setTrip(data);

        // Set initial states based on trip status
        if (data.status === "PICKUP_REACHED") {
          setEtaProgress(100);
        }

      } catch (error) {
        console.error("Error fetching trip details:", error);
        const message = getErrorMessage(error);
        openToast(message || "Failed to fetch trip details", 5000, "danger");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTripDetails();

    // Request ETA data
    sendTripMessage({ event: "eta_fetch" });
  }, [tripId, sendTripMessage, openToast]);

  // Manage sharing message visibility across refreshes
  useEffect(() => {
    // Cleanup if trip is finished or cancelled
    if (trip && ["COMPLETED", "CANCELLED"].includes(trip.status)) {
      localStorage.removeItem(`sharingMessageShown_${tripId}`);
    }

    // Reset negotiation options if status changes
    if (trip?.status !== "PENDING_ACCEPT") {
      setShowNegotiationOptions(false);
    }
  }, [trip, trip?.status, tripId]);

  // Handle trip WebSocket messages
  useEffect(() => {
    if (!tripMessage) return;

    if (tripMessage?.type === "chat_message") {
      const newMessage = JSON.parse(tripMessage?.data);
      const toastMessage = (newMessage.text) ? `New Message: ${newMessage.text}` : (newMessage.image) ? `You have a new message!` : "";
      appendChatToIndexedDB(newMessage);
      playNotificationSound();
      openToast(toastMessage, 5000, "info");
    }
    else if (tripMessage?.type === "chat_history") {
      updateChatHistoryInIndexedDB(tripMessage?.data).then((hasNewMessages) => {
        if (hasNewMessages) {
          playNotificationSound();
          openToast("You have new messages!", 5000, "info");
        }
      }).catch(console.error);
    }

    if (tripMessage?.type === "error_occurred") {
      const errorMessages = tripMessage.data;
      console.error("Error occurred:", errorMessages);
      openToast(errorMessages[0] || "An error occurred", 5000, "danger");
      return;
    }

    const incoming = tripMessage?.data || {};

    setTrip(prev => {
      if (!prev) return incoming;

      return {
        ...prev,
        ...Object.fromEntries(
          Object.entries(incoming).filter(([_, value]) => value !== undefined && value !== null)
        )
      };
    });
  }, [navigate, openToast, playNotificationSound, tripMessage]);

  // Handle ETA WebSocket messages
  useEffect(() => {
    if (!tripMessage) return;

    switch (tripMessage.type) {
      case "eta_update":
        const eta = tripMessage.data?.eta ?? DEFAULT_ESTIMATED_TIME;
        setEstimatedTime(eta);
        storeETA(tripId, eta);
        setIsCalculatingEta(false);
        break;
      case "pickup_reached":
        setEtaProgress(100);
        break;
      default:
        break;
    }
  }, [tripId, tripMessage]);

  // Handle ETA progress timer for IN_TRANSIT trips
  useEffect(() => {
    // Clear only local interval to avoid affecting responseTimer
    cleanup(intervalRef);

    if (!trip) return;

    if (trip?.status === "PICKUP_REACHED") {
      setEtaProgress(100);
      return;
    }

    if (estimatedTime <= 0) {
      setTimeElapsed(0);
      setEtaProgress(100);
      return;
    }

    // Calculate initial elapsed time
    const initialElapsed = Math.min(
      calculateTimeSinceUpdate(trip.updated_time, DEFAULT_ESTIMATED_TIME),
      estimatedTime
    );

    setTimeElapsed(initialElapsed);
    setEtaProgress((initialElapsed / estimatedTime) * 100);

    // Start interval if not completed
    if (initialElapsed < estimatedTime) {
      intervalRef.current = setInterval(() => {
        setTimeElapsed(prev => {
          const next = prev + 1;
          setEtaProgress((next / estimatedTime) * 100);
          return next;
        });
      }, 1000);
    }

    return () => cleanup(intervalRef);
  }, [trip?.status, trip?.updated_time, estimatedTime, trip, cleanup]);

  // Handle response timer for PENDING_ACCEPT and USER_CONFIRMATION
  useEffect(() => {
    cleanup(responseTimerRef);

    if (!trip || !["PENDING_ACCEPT", "USER_CONFIRMATION", "DRIVER_RESPONSE"].includes(trip.status)) {
      setRemainingSeconds(MAX_RESPONSE_TIME);
      return;
    }

    const remainingResponseTime = Math.max(
      0,
      MAX_RESPONSE_TIME - calculateTimeSinceUpdate(trip.updated_time, 0)
    );
    setRemainingSeconds(remainingResponseTime);

    responseTimerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          handleCancel();
          openToast("You took too long. Trip cancelled", 5000, "danger");
          handleNavigation("/booking", 3);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => cleanup(responseTimerRef);
  }, [handleCancel, handleNavigation, openToast, trip?.status, trip?.updated_time, cleanup, trip]);

  // Update price based on trip status and handle status-specific actions
  useEffect(() => {
    if (!trip?.status) return;

    switch (trip.status) {
      case "PENDING_ACCEPT":
        break;

      case "DRIVER_RESPONSE":
        break;

      case "USER_CONFIRMATION":
        break;

      case "IN_TRANSIT":
        if (trip.price_offer && trip.final_price && !sharingMessageShown) {
          const sharedValue = trip.price_offer - trip.final_price + 1;
          openToast(`Driver is sharing RM ${sharedValue} with you!`);
          setSharingMessageShown(true);
          localStorage.setItem(`sharingMessageShown_${tripId}`, "true");
        }
        subscribeToPush();
        break;

      case "PICKUP_REACHED":
        setEtaProgress(100);
        playNotificationSound();
        clearStoredETA(tripId);
        openToast("Driver has reached the pickup location. Please get ready to board the vehicle.");
        break;

      case "COMPLETED":
        openToast("Ride Complete! You will be returned to the homepage.", 5000, "success");
        clearAllChatsFromIndexedDB();
        clearStoredETA(tripId);
        handleNavigation("/booking", 3);
        break;

      case "CANCELLED":
        openToast("Trip cancelled. You will be returned to the homepage.", 5000, "danger");
        clearAllChatsFromIndexedDB();
        clearStoredETA(tripId);
        handleNavigation("/booking", 3);
        break;

      default:
        break;
    }
  }, [
    trip?.status,
    playNotificationSound,
    handleNavigation,
    tripId,
    sharingMessageShown,
    openToast
  ]);

  // Update info card content based on trip status
  useEffect(() => {
    if (!trip) return;

    switch (trip.status) {
      case "PENDING_ACCEPT":
        setInfoCard({
          title: "Driver Found",
          subtitle: null
        });
        break;

      case "USER_CONFIRMATION":
        setInfoCard({
          title: "Driver Negotiation Price",
          subtitle: "Driver has made an offer!"
        });
        break;

      case "DRIVER_RESPONSE":
        setInfoCard({
          title: "Waiting for Driver's Response...",
          subtitle: "Your ride could be confirmed in any second!"
        });
        break;

      case "IN_TRANSIT":
        if (isCalculatingEta) {
          setInfoCard({
            title: "Finding best route...",
            subtitle: "On The Way to pickup"
          });
        } else {
          const remainingTime = Math.max(0, Math.ceil((estimatedTime - timeElapsed) / 60));
          setInfoCard({
            title: `Estimated Time Arrival: ${remainingTime} Min`,
            subtitle: "On The Way to pickup"
          });
        }
        break;

      case "PICKUP_REACHED":
        setInfoCard({
          title: "Driver has reached pickup location",
          subtitle: "Please get ready to board the vehicle"
        });
        break;

      case "COMPLETED":
        setInfoCard({
          title: "Ride Completed",
          subtitle: "Thank you for using our service!"
        });
        break;

      case "CANCELLED":
        setInfoCard({
          title: "Trip Cancelled",
          subtitle: "It seems like your trip has been cancelled."
        });
        break;

      default:
        setInfoCard({
          title: "Unknown Status",
          subtitle: "Please wait..."
        });
    }
  }, [trip, estimatedTime, timeElapsed, isCalculatingEta]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Loading state
  if (isLoading || !trip) {
    return (
      <div className={classes.container}>
        <div className={classes.innerCardContent}>
          <p>Loading trip details...</p>
        </div>
      </div>
    );
  }

  const canNegotiate = price - 1 >= MIN_NEGOTIATION_PRICE;
  const canNegotiate2 = price - 2 >= MIN_NEGOTIATION_PRICE;

  return (
    <div className={classes.container}>
      <div className={classes.innerCardContent}>
        {/* Title Section */}
        {!["IN_TRANSIT", "PICKUP_REACHED"].includes(trip.status) && (
          <div className={classes.titleSection}>
            <p className={classes.title}>
              {trip.status === "DRIVER_RESPONSE" ? "Waiting for Driver's Response" : "Driver Matched"}
            </p>
            <p className={classes.subtitle}>
              {trip.status === "DRIVER_RESPONSE" ? (
                "Ride will be confirmed in 30 seconds!"
              ) : isCalculatingEta ? (
                "Finding best route..."
              ) : (
                <>
                  Only <b>{Math.ceil((estimatedTime - timeElapsed) / 60)} min</b> away...
                </>
              )}
            </p>
            <div className={classes.loadingBarContainer}>
              <LoadingBar
                // isLoading={trip.status === "DRIVER_RESPONSE"}
                // fakeLoading={false}
                progress={
                  ["PENDING_ACCEPT", "USER_CONFIRMATION", "DRIVER_RESPONSE"].includes(trip.status)
                    ? (remainingSeconds / MAX_RESPONSE_TIME) * 100
                    : 0
                }
              />
            </div>
            {["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status) && (
              <p className={classes.timer}>
                <Clock size={16} className={classes.timerIcon} />
                <b>{remainingSeconds}s</b> remaining to respond
              </p>
            )}
            {["DRIVER_RESPONSE"].includes(trip.status) && (
              <p className={classes.timer}>
                <Clock size={16} className={classes.timerIcon} />
                Driver will respond in<b>{remainingSeconds}s</b>
              </p>
            )}
          </div>
        )}

        {/* Driver info card */}
        <FormCard className={classes.driverInfoCard}>
          <img
            src={`${imageUrl}${trip.driver_details?.profile_picture}`}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = defaultPfp;
            }}
            className={classes.driverProfilePicture}
            alt="Driver profile"
          />
          <div className={classes.driverDetails}>
            <p className={classes.driverNameGender}>
              {trip.driver_details?.name || "Unknown"} ({trip.driver_details?.gender || "Unknown"})
            </p>
          </div>
          <div className={classes.carDetails}>
            <span className={classes.driverPlateNumber}>
              {trip.driver_details?.car_plate_number || "Unknown"}
            </span>
            <span className={classes.driverCarColorModel}>
              {trip.driver_details?.car_color || "Unknown"} • {trip.driver_details?.car_model || "Unknown"}
            </span>
          </div>
        </FormCard>

        {/* Order info card */}
        <FormCard className={classes.orderCard}>
          <div className={classes.cardContent}>
            <div className={classes.locationsImage}>
              <RouteConnector />
            </div>
            <div className={classes.rightContent}>
              {/* From Row */}
              <div className={classes.row}>
                <div className={classes.locationBox}>
                  <p className={classes.locationAddress}>
                    {trip.from_location || "Unknown"}
                  </p>
                </div>
                <div className={classes.paxInfo}>
                  <p className={classes.pax}>PAX {trip.pax || 0}</p>
                </div>
              </div>

              {/* Negotiation Buttons (Between Locations) */}
              {["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status) && (
                <div className={classes.negotiationSection}>
                  <RoundedEdgeButton
                    className={classes.acceptButton}
                    onClick={handleAccept}
                  >
                    <span className={classes.btnMainText}>Accept RM {Number(price).toFixed(2)}</span>
                    {price === trip.price_offer && (
                      <span className={classes.btnSubText}>
                        {(trip.price_offer !== trip.base_price) ? "Peak Fare" : "Base Fare"}
                      </span>
                    )}
                  </RoundedEdgeButton>

                  {trip.status === "PENDING_ACCEPT" && !showNegotiationOptions && (price > MIN_NEGOTIATION_PRICE) && (
                    <RoundedEdgeButton
                      className={classes.negotiateButton}
                      onClick={() => setShowNegotiationOptions(true)}
                    >
                      Negotiate
                    </RoundedEdgeButton>
                  )}

                  {trip.status === "PENDING_ACCEPT" && showNegotiationOptions && (
                    <div className={classes.negotiationButtons}>
                      {(canNegotiate || canNegotiate2) && <div className={classes.orDivider}>or</div>}

                      {canNegotiate && (
                        <RoundedEdgeButton
                          className={classes.negoButton1}
                          onClick={() => handleNegotiate(1)}
                        >
                          <span className={classes.btnMainText}>RM {price - 1}</span>
                          <span className={classes.btnSubText}>Recommended</span>
                        </RoundedEdgeButton>
                      )}
                      {(canNegotiate2 && trip.status !== "DRIVER_RESPONSE") && (
                        <RoundedEdgeButton
                          className={classes.negoButton2}
                          onClick={() => handleNegotiate(2)}
                        >
                          <span className={classes.btnMainText}>RM {price - 2}</span>
                          <span className={classes.btnSubText}>Lower chance</span>
                        </RoundedEdgeButton>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Separator if no buttons */}
              {!["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status) && (
                <hr className={classes.separator} />
              )}

              {/* To Row */}
              <div className={classes.row}>
                <div className={classes.locationBox}>
                  <p className={classes.locationAddress}>
                    {trip.to_location || "Unknown"}
                  </p>
                </div>
                <div className={classes.priceInfo}>
                  <p className={classes.price}>RM {Number(price).toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          {["IN_TRANSIT", "PICKUP_REACHED"].includes(trip.status) && (
            <div className={classes.etaSection}>
              <p className={classes.etaTitle}>
                {trip.status === "IN_TRANSIT" ? "ESTIMATED TIME ARRIVAL" : "ON THE WAY TO DROPOFF"}
              </p>
              {trip.status === "IN_TRANSIT" && (
                <>
                  <p className={classes.etaTime}>{Math.max(0, Math.ceil((estimatedTime - timeElapsed) / 60))} min</p>
                  <LoadingBar progress={etaProgress} />
                </>
              )}

              <div className={classes.actionButtonsContainer}>
                <PillButton
                  className={classes.actionButton}
                  onClick={() => navigate(`/chat`)}
                >
                  <img src={chatIcon} alt="chat" className={classes.btnIcon} />
                  <span>Chat</span>
                </PillButton>
                <PillButton
                  className={classes.actionButton}
                  onClick={() => navigate(`/profile/${trip.driver}`, { state: { profileData: trip.driver_details } })}
                >
                  <img src={profileIcon} alt="profile" className={classes.btnIcon} />
                  <span>Driver Profile</span>
                </PillButton>
              </div>
            </div>
          )}

          {/* Cancel Button */}
          {["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status) && (
            <>
              <hr className={classes.cancelSeparator} />
              <RoundedEdgeButton
                className={classes.cancelButton}
                onClick={handleCancel}
              >
                Cancel
              </RoundedEdgeButton>
            </>
          )}
        </FormCard>

        {/* Success Message for Negotiation Sent */}
        {trip.status === "DRIVER_RESPONSE" && (
          <div className={classes.negotiationSuccess}>
            <CheckCircle size={16} className={classes.successIcon} />
            <span className={classes.successText}>Negotiated fare successfully sent to driver</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverFound;
